// Format date to DD-MM-YYYY
const formatDate = (dateString: string | undefined | null): string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;

  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
};

export default formatDate;

const parseDate = (dateString: string): Date | null => {
  if (!dateString) return null;

  if (/^\d{2}\/\d{2}\/\d{2}$/.test(dateString)) {
    const [yearStr, monthStr, dayStr] = dateString.split("/");
    const year = parseInt(yearStr, 10) + 2000;
    const month = parseInt(monthStr, 10) - 1;
    const day = parseInt(dayStr, 10);
    const date = new Date(year, month, day);

    if (
      !isNaN(date.getTime()) &&
      date.getFullYear() === year &&
      date.getMonth() === month &&
      date.getDate() === day
    ) {
      return date;
    }
  }

  if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
    const [dayStr, monthStr, yearStr] = dateString.split("-");
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10) - 1;
    const day = parseInt(dayStr, 10);
    const date = new Date(year, month, day);

    if (
      !isNaN(date.getTime()) &&
      date.getFullYear() === year &&
      date.getMonth() === month &&
      date.getDate() === day
    ) {
      return date;
    }
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date;
    }
  }

  console.error(`Unknown or invalid date format: ${dateString}`);
  return null;
};

export const formatDateToLongString = (
  dateString: string | undefined | null
): string => {
  if (!dateString) return "";

  const date = parseDate(dateString);
  if (!date) {
    return dateString;
  }

  const options: Intl.DateTimeFormatOptions = {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  };

  return date.toLocaleDateString("en-GB", options);
};

export const formatDateForDisplay = (date: Date): string => {
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
};

export const formatTime = (timeString?: string) => {
  if (!timeString) return "";
  return `${timeString.substring(0, 5)} HRS (CAT)`;
};

export const formatToDateString = (dateString: string) => {
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleDateString("en-US", { month: "long" });
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

export const formatLifespan = (dob?: string, dod?: string) => {
  if (!dob || !dod) return "";
  return `${formatToDateString(dob)} - ${formatToDateString(dod)}`;
};

const SHORT_MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sept", "Oct", "Nov", "Dec",
];

const SHORT_WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thur", "Fri", "Sat"];

// e.g. "Thur 24 Sept 2026"
export const formatDateWithWeekday = (dateString?: string | null): string => {
  if (!dateString) return "";
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString);
  const date = ymd
    ? new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]))
    : new Date(dateString);
  if (isNaN(date.getTime())) return dateString;

  const weekday = SHORT_WEEKDAYS[date.getDay()];
  const day = date.getDate();
  const month = SHORT_MONTHS[date.getMonth()];
  const year = date.getFullYear();

  return `${weekday} ${day} ${month} ${year}`;
};

export const formatISODateTime = (isoString: string | null | undefined): string => {
  if (!isoString) return "";
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return isoString;

  const weekday = date.toLocaleDateString("en-GB", { weekday: "long" });
  const day = date.getDate();
  const month = date.toLocaleDateString("en-GB", { month: "long" });
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");

  return `${weekday} ${day} ${month} ${year} at ${hours}${minutes}`;
};
